import { type ReactElement, type ReactNode, type ThHTMLAttributes } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '@/lib/utils';

/** Active sort direction for a column header. */
export type SortDirection = 'asc' | 'desc';

type DataTableThProps = Omit<ThHTMLAttributes<HTMLTableCellElement>, 'align'> & {
  /**
   * When true, appends stacked up/down chevrons as a sort affordance. Pass
   * `onSort` alongside to make the header an interactive sort control;
   * without it the chevrons are purely decorative.
   */
  readonly sortable?: boolean;
  readonly widthClassName?: string;
  readonly align?: 'left' | 'right' | 'center';
  /**
   * Active sort direction for THIS column, or `null` when it isn't the
   * active sort column. Only meaningful together with `onSort`.
   */
  readonly sortDirection?: SortDirection | null;
  /**
   * When provided (with `sortable`), the header becomes an interactive sort
   * control: it renders a `<button>`, invokes this on click, and reflects
   * the current `sortDirection` via `aria-sort` + highlighted chevrons.
   */
  readonly onSort?: () => void;
};

function SortChevrons({
  direction,
}: {
  readonly direction: SortDirection | null | undefined;
}): ReactElement {
  return (
    <span
      className="inline-flex shrink-0 flex-col text-muted-foreground/90"
      aria-hidden="true"
    >
      <ChevronUp
        className={cn('h-3 w-3 -mb-1', direction === 'asc' ? 'text-foreground' : 'opacity-70')}
      />
      <ChevronDown
        className={cn('h-3 w-3 -mt-1', direction === 'desc' ? 'text-foreground' : 'opacity-70')}
      />
    </span>
  );
}

export function DataTableTh({
  children,
  sortable = false,
  className,
  widthClassName,
  align = 'left',
  sortDirection,
  onSort,
  ...rest
}: DataTableThProps): ReactElement {
  const interactive = sortable && onSort !== undefined;
  const ariaSort: ThHTMLAttributes<HTMLTableCellElement>['aria-sort'] = interactive
    ? sortDirection === 'asc'
      ? 'ascending'
      : sortDirection === 'desc'
        ? 'descending'
        : 'none'
    : undefined;

  const innerClass = cn(
    'inline-flex items-center gap-1.5 whitespace-nowrap',
    align === 'right' && 'ms-auto w-full min-w-0 flex-nowrap justify-end',
  );

  let inner: ReactNode;
  if (interactive) {
    inner = (
      <button
        type="button"
        onClick={onSort}
        className={cn(
          innerClass,
          'cursor-pointer rounded-sm select-none uppercase tracking-[inherit] font-[inherit]',
          'hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          sortDirection != null && 'text-foreground',
        )}
      >
        {children}
        <SortChevrons direction={sortDirection} />
      </button>
    );
  } else if (sortable) {
    inner = (
      <span className={innerClass}>
        {children}
        <SortChevrons direction={null} />
      </span>
    );
  } else {
    inner = children;
  }

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={cn(
        'align-middle',
        'px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] first:pl-5 last:pr-5 text-muted-foreground',
        widthClassName,
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
      {...rest}
    >
      {inner}
    </th>
  );
}
